import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import GeoLocationInput from "@/components/GeoLocationInput";
import FileUploadZone from "@/components/FileUploadZone";
import DichiarazioniBlock from "@/components/DichiarazioniBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import DroneIcon from "@/components/DroneIcon";
import { FI, YN } from "@/components/FormFields";
import { StepProgress, FormNavigation } from "@/components/StepProgress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Anagrafica", "Assicurazioni Precedenti", "Condizioni e Attività", "Dettagli Apparecchi", "Operatori", "Estensioni e Prodotti", "Sinistrosità", "Dichiarazioni"];

const UTILIZZI = [
  "Fotografia / Riprese videografiche",
  "Vigilanza / Videosorveglianza",
  "Gestione delle colture",
  "Monitoraggio",
  "Perizie",
  "Gestione delle catastrofi",
  "Ricerca e salvataggio",
  "Altro",
];

let _uid = 0;
const uid = () => `item_${++_uid}`;

const EMPTY_DRONE = { _id: "", marca: "", modello: "", targa_serie: "", valore: "", carico_rimovibile: "", peso_max: "", autocostruito: "", ore_volo: "", territorio: "", oltre_visuale: "" };
const EMPTY_OPERATORE = { _id: "", nome_cognome: "", corso_accreditato: "", esperienza_10h: "" };
const EMPTY_SINISTRO = { _id: "", data: "", valore: "", marca: "", modello: "", descrizione: "" };

export default function DroniPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [droni, setDroni] = useState([{ ...EMPTY_DRONE, _id: uid() }]);
  const [operatori, setOperatori] = useState([{ ...EMPTY_OPERATORE, _id: uid() }]);
  const [sinistri, setSinistri] = useState([]);
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const update = (k, v) => setFormData(prev => ({ ...prev, [k]: v }));
  const handleFileUpload = (k, d) => { setFiles(prev => { const n = { ...prev }; if (d) n[k] = d; else delete n[k]; return n; }); };

  const updateDrone = (i, k, v) => setDroni(prev => prev.map((d, j) => j === i ? { ...d, [k]: v } : d));
  const addDrone = () => setDroni(prev => [...prev, { ...EMPTY_DRONE, _id: uid() }]);
  const removeDrone = (i) => setDroni(prev => prev.filter((_, j) => j !== i));

  const updateOperatore = (i, k, v) => setOperatori(prev => prev.map((o, j) => j === i ? { ...o, [k]: v } : o));
  const addOperatore = () => setOperatori(prev => [...prev, { ...EMPTY_OPERATORE, _id: uid() }]);
  const removeOperatore = (i) => setOperatori(prev => prev.filter((_, j) => j !== i));

  const updateSinistro = (i, k, v) => setSinistri(prev => prev.map((s, j) => j === i ? { ...s, [k]: v } : s));

  const totalSteps = STEPS.length;
  const progress = ((step + 1) / totalSteps) * 100;

  const handleSubmit = async () => {
    if (!formData.nome || !formData.cognome || !formData.email || !formData.telefono) {
      toast.error("Compila tutti i campi obbligatori dell'anagrafica"); return;
    }
    if (!formData.privacy) { toast.error("Devi accettare la privacy policy"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        type: "droni", data: { ...formData, droni, operatori, sinistri },
        files: Object.values(files),
        contact: { nome: formData.nome, cognome: formData.cognome, email: formData.email, telefono: formData.telefono }
      });
      navigate("/conferma");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nell'invio."); }
    finally { setSubmitting(false); }
  };

  const renderStep = () => {
    switch (step) {
      case 0: return renderAnagrafica();
      case 1: return renderAssicPrec();
      case 2: return renderCondizioni();
      case 3: return renderApparecchi();
      case 4: return renderOperatori();
      case 5: return renderEstensioni();
      case 6: return renderSinistri();
      case 7: return renderDichiarazioni();
      default: return null;
    }
  };

  const renderAnagrafica = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Dati anagrafici del proponente/contraente.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FI label="Nome" required value={formData.nome} onChange={v => update("nome", v)} tid="dr-nome" />
        <FI label="Cognome" required value={formData.cognome} onChange={v => update("cognome", v)} tid="dr-cognome" />
      </div>
      <FI label="Ragione Sociale (se azienda)" value={formData.ragione_sociale} onChange={v => update("ragione_sociale", v)} tid="dr-rag-soc" />
      <GeoLocationInput label="Sede Legale o Residenza" required value={formData.indirizzo} onChange={v => update("indirizzo", v)} testId="dr-indirizzo" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FI label="Citt&agrave;" required value={formData.citta} onChange={v => update("citta", v)} tid="dr-citta" />
        <FI label="Provincia" value={formData.provincia} onChange={v => update("provincia", v)} tid="dr-prov" />
        <FI label="CAP" value={formData.cap} onChange={v => update("cap", v)} tid="dr-cap" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FI label="Codice Fiscale / P.IVA" required value={formData.cf_piva} onChange={v => update("cf_piva", v.toUpperCase())} tid="dr-cf" />
        <FI label="Paese" value={formData.paese} onChange={v => update("paese", v)} tid="dr-paese" placeholder="Italia" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FI label="Email" required type="email" value={formData.email} onChange={v => update("email", v)} tid="dr-email" placeholder="la.tua@email.it" />
        <FI label="Telefono cellulare" required type="tel" value={formData.telefono} onChange={v => update("telefono", v)} tid="dr-tel" placeholder="+39 333 1234567" />
      </div>
    </div>
  );

  const renderAssicPrec = () => (
    <div className="space-y-5">
      <YN label="Il Proponente &egrave; mai stato assicurato per questa tipologia di rischio?" field="gia_assicurato" formData={formData} update={update} tid="dr-gia-ass" />
      {formData.gia_assicurato === "si" && (
        <div className="space-y-4 pl-4 border-l-2 border-[#163321]/10">
          <FI label="Assicuratori precedenti" required value={formData.assic_prec} onChange={v => update("assic_prec", v)} tid="dr-assic-prec" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FI label="Franchigia" value={formData.franchigia_prec} onChange={v => update("franchigia_prec", v)} tid="dr-franch-prec" />
            <FI label="Massimale" value={formData.massimale_prec} onChange={v => update("massimale_prec", v)} tid="dr-mass-prec" />
          </div>
          <FI label="Premio polizza in scadenza (&euro;)" required type="number" value={formData.ultimo_premio} onChange={v => update("ultimo_premio", v)} tid="dr-ultimo-premio" />
          <FI label="Data scadenza ultima copertura" type="date" value={formData.data_scad} onChange={v => update("data_scad", v)} tid="dr-data-scad" />
          <FI label="Numero anni copertura continua" type="number" value={formData.anni_cop} onChange={v => update("anni_cop", v)} tid="dr-anni-cop" />
        </div>
      )}
      <YN label="Vi &egrave; mai stata rifiutata o cancellata una polizza negli ultimi 5 anni?" field="polizza_rifiutata" formData={formData} update={update} tid="dr-rifiutata" />
    </div>
  );

  const renderCondizioni = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Condizioni di polizza e informazioni sull'attivit&agrave;.</p>
      <FI label="Numero droni in volo simultaneamente" required type="number" value={formData.num_droni_simultanei} onChange={v => update("num_droni_simultanei", v)} tid="dr-num-sim" />
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Franchigia RCT</Label>
        <Select value={formData.franchigia_rct || ""} onValueChange={v => update("franchigia_rct", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="dr-franchigia"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="150">&euro; 150</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Massimale RCT <span className="text-[#D97B58]">*</span></Label>
        <Select value={formData.massimale_rct || ""} onValueChange={v => update("massimale_rct", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="dr-massimale"><SelectValue placeholder="Seleziona massimale" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1000000">&euro; 1.000.000</SelectItem>
            <SelectItem value="2000000">&euro; 2.000.000</SelectItem>
            <SelectItem value="5000000">&euro; 5.000.000</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Utilizzo principale <span className="text-[#D97B58]">*</span></Label>
        <Select value={formData.utilizzo || ""} onValueChange={v => update("utilizzo", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="dr-utilizzo"><SelectValue placeholder="Seleziona utilizzo" /></SelectTrigger>
          <SelectContent>
            {UTILIZZI.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {formData.utilizzo === "Altro" && (
        <FI label="Specificare utilizzo" required value={formData.utilizzo_altro} onChange={v => update("utilizzo_altro", v)} tid="dr-utilizzo-altro" />
      )}
      <YN label="Tutti gli apparecchi sono utilizzati per attivit&agrave; commerciali o professionali?" field="uso_professionale" formData={formData} update={update} tid="dr-uso-prof" />
      {formData.uso_professionale === "no" && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          Non &egrave; possibile procedere alla quotazione per apparecchi ad uso non professionale o utilizzati per competizioni e gare.
        </div>
      )}
    </div>
  );

  const renderApparecchi = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Dettagli di ogni apparecchio per cui si richiede copertura.</p>
      {droni.map((drone, i) => (
        <div key={i} className="p-5 bg-[#F5F4F0] rounded-2xl border border-[#E5E2DB] space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-[#0F1F14]">Drone {i + 1}</p>
            {droni.length > 1 && (
              <button type="button" onClick={() => removeDrone(i)} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1" data-testid={`dr-remove-drone-${i}`}>
                <Trash2 className="w-3 h-3" /> Rimuovi
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FI label="Marca" required value={drone.marca} onChange={v => updateDrone(i, "marca", v)} tid={`dr-drone-${i}-marca`} />
            <FI label="Modello" required value={drone.modello} onChange={v => updateDrone(i, "modello", v)} tid={`dr-drone-${i}-modello`} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FI label="Targa / Numero di Serie" value={drone.targa_serie} onChange={v => updateDrone(i, "targa_serie", v)} tid={`dr-drone-${i}-serie`} />
            <FI label="Valore del drone (&euro;)" type="number" required value={drone.valore} onChange={v => updateDrone(i, "valore", v)} tid={`dr-drone-${i}-valore`} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FI label="Peso max al decollo incl. carico (kg)" type="number" value={drone.peso_max} onChange={v => updateDrone(i, "peso_max", v)} tid={`dr-drone-${i}-peso`} />
            <FI label="Stima annuale ore di volo" type="number" value={drone.ore_volo} onChange={v => updateDrone(i, "ore_volo", v)} tid={`dr-drone-${i}-ore`} />
          </div>
          <FI label="Principale territorio di impiego" value={drone.territorio} onChange={v => updateDrone(i, "territorio", v)} tid={`dr-drone-${i}-territorio`} placeholder="es. Italia" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <YN label="Carico rimovibile?" field="carico_rimovibile" formData={drone} update={(k, v) => updateDrone(i, k, v)} tid={`dr-drone-${i}-carico`} />
            <YN label="Autocostruito?" field="autocostruito" formData={drone} update={(k, v) => updateDrone(i, k, v)} tid={`dr-drone-${i}-autoc`} />
            <YN label="Oltre linea visuale?" field="oltre_visuale" formData={drone} update={(k, v) => updateDrone(i, k, v)} tid={`dr-drone-${i}-bvlos`} />
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={addDrone} className="w-full rounded-xl border-dashed border-[#E5E2DB] text-[#4A5E51] hover:bg-[#F5F4F0]" data-testid="dr-add-drone">
        <Plus className="w-4 h-4 mr-2" /> Aggiungi apparecchio
      </Button>
    </div>
  );

  const renderOperatori = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Dettagli degli operatori per cui si richiede copertura.</p>
      {operatori.map((op, i) => (
        <div key={i} className="p-5 bg-[#F5F4F0] rounded-2xl border border-[#E5E2DB] space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-[#0F1F14]">Operatore {i + 1}</p>
            {operatori.length > 1 && (
              <button type="button" onClick={() => removeOperatore(i)} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1" data-testid={`dr-remove-op-${i}`}>
                <Trash2 className="w-3 h-3" /> Rimuovi
              </button>
            )}
          </div>
          <FI label="Nome e Cognome" required value={op.nome_cognome} onChange={v => updateOperatore(i, "nome_cognome", v)} tid={`dr-op-${i}-nome`} />
          <YN label="Ha superato un corso di formazione accreditato dall'Autorit&agrave; dell'Aviazione Civile (ENAC) o ente equivalente?" field="corso_accreditato" formData={op} update={(k, v) => updateOperatore(i, k, v)} tid={`dr-op-${i}-corso`} />
          <YN label="Ha almeno 10 ore di esperienza di volo SAPR e/o ha completato un corso di formazione organizzato dal costruttore?" field="esperienza_10h" formData={op} update={(k, v) => updateOperatore(i, k, v)} tid={`dr-op-${i}-exp`} />
        </div>
      ))}
      <Button type="button" variant="outline" onClick={addOperatore} className="w-full rounded-xl border-dashed border-[#E5E2DB] text-[#4A5E51] hover:bg-[#F5F4F0]" data-testid="dr-add-op">
        <Plus className="w-4 h-4 mr-2" /> Aggiungi operatore
      </Button>
    </div>
  );

  const renderEstensioni = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Estensioni di copertura e prodotti addizionali.</p>
      <YN label="Estensione 1 - Responsabilit&agrave; nei confronti degli operatori del SAPR" field="est_resp_operatori" formData={formData} update={update} tid="dr-est-1" />
      <YN label="Estensione 2 - Responsabilit&agrave; legale per sostanze chimiche" field="est_resp_chimiche" formData={formData} update={update} tid="dr-est-2" />

      <div className="border-t border-[#E5E2DB] pt-5 mt-5">
        <YN label="Desidera includere il prodotto addizionale &quot;Corpi Drone&quot; (copertura danni al drone)?" field="corpi_drone" formData={formData} update={update} tid="dr-corpi" />
      </div>

      {formData.corpi_drone === "si" && (
        <div className="space-y-5 pl-4 border-l-2 border-[#163321]/10">
          <p className="text-sm text-[#4A5E51] font-medium">Pezzi di ricambio / Attrezzatura ausiliaria</p>
          <FI label="Valore totale pezzi di ricambio e attrezzatura da assicurare (&euro;)" type="number" value={formData.valore_ricambi} onChange={v => update("valore_ricambi", v)} tid="dr-val-ricambi" />
          <div className="space-y-2">
            <Label className="text-sm text-[#0F1F14]">Dettagli pezzi con valore superiore a &euro;10.000</Label>
            <Textarea value={formData.det_ricambi || ""} onChange={e => update("det_ricambi", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[60px]" placeholder="Tipo pezzo - Somma assicurata (uno per riga)" data-testid="dr-det-ricambi" />
          </div>
          <p className="text-sm text-[#4A5E51] font-medium">Carico rimovibile</p>
          <div className="space-y-2">
            <Label className="text-sm text-[#0F1F14]">Dettagli carichi rimovibili da coprire</Label>
            <Textarea value={formData.carichi_rimovibili || ""} onChange={e => update("carichi_rimovibili", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[60px]" placeholder="Marca - Modello - N. Serie - Somma assicurata (uno per riga)" data-testid="dr-carichi" />
          </div>
        </div>
      )}
    </div>
  );

  const renderSinistri = () => {
    if (formData.sinistri_5 === "si" && sinistri.length === 0) {
      setSinistri([{ ...EMPTY_SINISTRO }]);
    }
    return (
    <div className="space-y-5">
      <YN label="Ha avuto sinistri che riguardano droni negli ultimi 5 anni?" field="sinistri_5" formData={formData} update={update} tid="dr-sinistri-5" />
      {formData.sinistri_5 === "si" && (
        <>
          {sinistri.map((s, i) => (
            <div key={`sinistro-${i}`} className="p-4 bg-[#F5F4F0] rounded-xl border border-[#E5E2DB] space-y-3">
              <p className="text-sm font-medium text-[#0F1F14]">Sinistro {i + 1}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FI label="Data" type="date" value={s.data} onChange={v => updateSinistro(i, "data", v)} tid={`dr-sin-${i}-data`} />
                <FI label="Valore approssimativo (&euro;)" type="number" value={s.valore} onChange={v => updateSinistro(i, "valore", v)} tid={`dr-sin-${i}-val`} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FI label="Marca" value={s.marca} onChange={v => updateSinistro(i, "marca", v)} tid={`dr-sin-${i}-marca`} />
                <FI label="Modello" value={s.modello} onChange={v => updateSinistro(i, "modello", v)} tid={`dr-sin-${i}-mod`} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-[#0F1F14]">Descrizione</Label>
                <Textarea value={s.descrizione || ""} onChange={e => updateSinistro(i, "descrizione", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[60px]" data-testid={`dr-sin-${i}-desc`} />
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => setSinistri(prev => [...prev, { ...EMPTY_SINISTRO }])} className="w-full rounded-xl border-dashed border-[#E5E2DB] text-[#4A5E51]" data-testid="dr-add-sin">
            <Plus className="w-4 h-4 mr-2" /> Aggiungi sinistro
          </Button>
        </>
      )}
      <FileUploadZone label="Upload documentazione sinistri" hint="Documenti relativi a sinistri pregressi (se applicabile)"
        required={false} onFileUploaded={d => handleFileUpload("sinistri_doc", d)} />
    </div>
    );
  };

  const renderDichiarazioni = () => (
    <div className="space-y-6">
      <p className="text-sm text-[#4A5E51]">Conferma le dichiarazioni e accetta le condizioni.</p>
      <DichiarazioniBlock formData={formData} update={update} />
      <div className="flex items-start gap-3 p-4 bg-[#F5F4F0] rounded-xl">
        <Checkbox id="dr-set-info" checked={formData.set_informativo || false} onCheckedChange={v => update("set_informativo", v)} className="mt-0.5 data-[state=checked]:bg-[#163321]" data-testid="dr-set-info" />
        <label htmlFor="dr-set-info" className="text-xs text-[#4A5E51] leading-relaxed cursor-pointer">
          <strong>Set Informativo e Obbligazioni Vincolanti</strong> <span className="text-[#D97B58]">*</span><br />
          Dichiaro di aver preso visione delle obbligazioni vincolanti: il SAPR sar&agrave; utilizzato solo per attivit&agrave; assicurate,
          l'operatore possieder&agrave; tutti i permessi richiesti, il peso max non superer&agrave; 110kg, e la funzione Return to Home sar&agrave; sempre attiva.
        </label>
      </div>
    </div>
  );

  return (
    <><Header />
    <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="droni-page">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-12">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
            <DroneIcon className="w-7 h-7 text-[#163321]" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F1F14] mb-2" data-testid="droni-title">Assicurazione Droni / SAPR</h1>
          <p className="text-sm text-[#4A5E51]">Copertura RC e Corpi per i tuoi apparecchi a pilotaggio remoto</p>
        </div>
        <StepProgress steps={STEPS} currentStep={step} onStepClick={setStep} progress={progress} testIdPrefix="dr" />
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 mb-6 shadow-sm" data-testid={`dr-step-${step}`}>
          <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-5">{STEPS[step]}</h2>
          {renderStep()}
        </div>
        <FormNavigation
          step={step} totalSteps={totalSteps}
          onPrev={() => step === 0 ? navigate("/") : setStep(s => s - 1)}
          onNext={() => setStep(s => s + 1)}
          onSubmit={handleSubmit} submitting={submitting} canSubmit={!!formData.privacy}
          prevLabel={step === 0 ? "Home" : "Indietro"} testIdPrefix="dr"
        />
      </div>
      <PrivacyLinks className="mt-6 mb-4" />
    </main><Footer /></>
  );
}
